import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Check, Copy } from 'lucide-react';
import { useHighlighted } from './highlight';

const cx = (...parts: Array<string | false | null | undefined>) => parts.filter(Boolean).join(' ');

/** Copies text to the clipboard and confirms for a moment. */
export function CopyButton({ text, className }: { text: string; className?: string }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard blocked (insecure context or permissions): nothing to do.
    }
  };
  return (
    <button
      type="button"
      onClick={copy}
      className={cx(
        'inline-flex min-h-8 items-center gap-1.5 rounded-md px-2 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground',
        className,
      )}
      aria-label={copied ? 'Copied' : 'Copy code'}
    >
      {copied ? (
        <Check className="size-3.5" aria-hidden />
      ) : (
        <Copy className="size-3.5" aria-hidden />
      )}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

/** Lines past which long files collapse behind "Show all". */
export const COLLAPSE_LINES = 60;

/**
 * Highlighted code with optional line numbers. The gutter sits outside the
 * horizontally scrolling area so numbers stay put on phones. Long code is
 * clipped with CSS (max-height on the 1.5rem line grid), not re-sliced, so
 * highlighting stays correct.
 */
export function CodeView({
  code,
  language,
  lineNumbers = false,
  collapsible = false,
  header,
}: {
  code: string;
  language: string;
  lineNumbers?: boolean;
  collapsible?: boolean;
  header?: ReactNode;
}) {
  const text = code.replace(/\n$/, '');
  const lineCount = text.split('\n').length;
  const highlighted = useHighlighted(text, language);
  const [expanded, setExpanded] = useState(false);
  const collapsed = collapsible && !expanded && lineCount > COLLAPSE_LINES;

  return (
    <div className="code-surface overflow-hidden rounded-lg border border-border">
      {header}
      <div className={cx('relative flex', collapsed && 'max-h-[90rem] overflow-hidden')}>
        {lineNumbers && (
          <pre
            aria-hidden
            className="shrink-0 select-none border-r border-border py-3 pl-3 pr-2 text-right text-muted-foreground/70"
          >
            {Array.from({ length: lineCount }, (_, i) => i + 1).join('\n')}
          </pre>
        )}
        <pre className="min-w-0 flex-1 overflow-x-auto px-3 py-3">
          <code className="hljs">{highlighted}</code>
        </pre>
        {collapsed && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-background/90 to-transparent" />
        )}
      </div>
      {collapsible && lineCount > COLLAPSE_LINES && (
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="flex min-h-10 w-full items-center justify-center border-t border-border text-sm font-medium text-primary hover:bg-muted"
          aria-expanded={expanded}
        >
          {expanded ? 'Show less' : `Show all ${lineCount} lines`}
        </button>
      )}
    </div>
  );
}
